import { differenceInDays } from 'date-fns'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { PriceExplanationMark } from '@components/PriceInfo/PriceExplanationMark'
import { getBasePrice, getPriceOrNull } from '../../helpers'
import { useBookingFormStore } from '../../providers/BookingFormStoreProvider'
import { IPrice, ITravelDate } from '../../store/data'
import DiscountedPrice from '../DiscountedPrice'
import Skeleton from '../Skeleton'
import Typography from '../Typography'

type PriceProps = {
  isFinalPrice?: boolean
  lowestPrice?: IPrice | null
  price?: IPrice | null
  travelDate: ITravelDate
  withPriceExplanation?: boolean
}

const Price: React.FunctionComponent<PriceProps> = ({
  isFinalPrice,
  lowestPrice,
  price,
  travelDate,
  withPriceExplanation = false,
}) => {
  const language = useBookingFormStore((state) => state.language)
  const finalAmount = price ? getPriceOrNull(price, 'amount', language) : null
  // Original = full base price (before ALL discounts), so the strikethrough matches
  // "Základní cena" and the saving equals the "Slevy" line.
  const finalOriginal = finalAmount !== null && price ? getBasePrice(price, language) : null
  const lowestAmountPerNight = lowestPrice
    ? getPriceOrNull(lowestPrice, 'amount_per_night', language)
    : null
  const lowestOriginalPerNight = lowestPrice
    ? getPriceOrNull(lowestPrice, 'original_amount_per_night', language)
    : null

  return (
    <div className="italic flex flex-col items-end pr-8">
      <Typography className="text-dark font-medium" variant="subcategory">
        {isFinalPrice ? (
          finalAmount !== null ? (
            <>
              <DiscountedPrice original={finalOriginal} value={finalAmount} />
              {withPriceExplanation && <PriceExplanationMark />}
            </>
          ) : (
            <Skeleton height={24} inline width={80} />
          )
        ) : (
          <>
            <span className="mr-1">
              <FormattedMessage
                defaultMessage="from"
                description="From price on Lowest price component"
              />
            </span>
            {lowestAmountPerNight !== null ? (
              <>
                {/*
                  Teaser only: show the regular "from" price WITHOUT the promo
                  strikethrough. The lowest price is a global minimum, so a
                  promotion in some season that isn't even in view (or currently
                  selectable) would otherwise advertise a discounted price the
                  guest can't get for these dates. The discount appears once a
                  concrete from–to range is picked (the isFinalPrice branch).
                */}
                <DiscountedPrice value={lowestOriginalPerNight ?? lowestAmountPerNight} />
                {withPriceExplanation && <PriceExplanationMark />}
              </>
            ) : (
              <Skeleton height={24} inline width={80} />
            )}
          </>
        )}
      </Typography>
      <Typography className="text-muted" variant="small">
        {isFinalPrice && travelDate.from && travelDate.to ? (
          <FormattedMessage
            defaultMessage="{count, plural, one {for # night} other {for # nights}}"
            description="Count nights in Booking details"
            values={{ count: differenceInDays(travelDate.to, travelDate.from) }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="/ night"
            description="night unit in Lowest price component"
          />
        )}
      </Typography>
    </div>
  )
}

export default Price
